import { API_BASE_URL } from '../../config';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function detectCurrentPushDeviceKind() {
  const ua = String(navigator.userAgent || '');
  const hasTouch = Number(navigator.maxTouchPoints || 0) > 1;
  const compactViewport = typeof window !== 'undefined' ? Number(window.innerWidth || 0) <= 1024 : false;
  const standalone = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false;
  return /Android|iPhone|iPad|Mobile/i.test(ua) || standalone || (hasTouch && compactViewport)
    ? 'mobile_web'
    : 'desktop_web';
}

export async function registerWebPushSubscription({ requestPermission = false, forceRefresh = false } = {}) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return { ok: false, reason: 'push_not_supported' };
  }

  if (requestPermission && Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'permission_denied' };
    }
  }

  if (Notification.permission !== 'granted') {
    return { ok: false, reason: 'permission_not_granted' };
  }

  const reg = await navigator.serviceWorker.register('/push-sw.js');

  const keyRes = await fetch(`${API_BASE_URL}/api/push/vapid-public-key`, {
    credentials: 'include',
    headers: getAuthHeaders()
  });
  if (!keyRes.ok) {
    return { ok: false, reason: 'vapid_key_fetch_failed' };
  }
  const keyData = await keyRes.json();
  const publicKey = String(keyData?.publicKey || '');
  if (!publicKey) {
    return { ok: false, reason: 'missing_vapid_key' };
  }

  let subscription = await reg.pushManager.getSubscription();
  if (subscription && forceRefresh) {
    const oldEndpoint = String(subscription.endpoint || '').trim();
    if (oldEndpoint) {
      try {
        await fetch(`${API_BASE_URL}/api/push/unsubscribe`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ endpoint: oldEndpoint })
        });
      } catch (_) {
        // no-op
      }
    }
    try { await subscription.unsubscribe(); } catch (_) { /* no-op */ }
    subscription = null;
  }

  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  }

  const deviceKind = detectCurrentPushDeviceKind();

  const subRes = await fetch(`${API_BASE_URL}/api/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      deviceKind
    })
  });

  if (!subRes.ok) {
    return { ok: false, reason: 'subscribe_api_failed' };
  }

  return { ok: true };
}

export async function testSelfWebPush() {
  const res = await fetch(`${API_BASE_URL}/api/push/test-self`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  if (!res.ok) {
    return { ok: false, reason: 'test_self_failed' };
  }
  return res.json();
}

export async function getServerPushStatus() {
  const res = await fetch(`${API_BASE_URL}/api/push/status`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...getAuthHeaders()
    }
  });
  if (!res.ok) {
    return {
      ok: false,
      reason: 'status_failed',
      activeCount: 0,
      hasServerActiveSubscription: false,
      deviceKinds: [],
      hasDesktopWebSubscription: false,
      hasMobileWebSubscription: false
    };
  }
  const data = await res.json();
  const activeCount = Number(data?.activeCount || 0);
  return {
    ok: true,
    activeCount,
    hasServerActiveSubscription: activeCount > 0,
    deviceKinds: Array.isArray(data?.deviceKinds) ? data.deviceKinds : [],
    hasDesktopWebSubscription: data?.hasDesktopWebSubscription === true,
    hasMobileWebSubscription: data?.hasMobileWebSubscription === true
  };
}
